import { apiClient } from '@applyonline/shared'
import {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeFundingDto,
  ComputeFundingResultDto,
  ComputeEstimatedPaymentResultDto,
  MonthlyPayment
} from '@applyonline/shared/types'
import { QueryFunctionContext, useQuery } from '@tanstack/react-query'

const AMOUNT_FOR_PROMOTIONAL_CREDIT_APP = 'amountForPromotionalCreditApp'
const ESTIMATED_PAYMENT = 'estimatedPayment'

const keysFactory = {
  amountForPromotionalCreditApp: (computeFunding: ComputeFundingDto) =>
    [{ scope: AMOUNT_FOR_PROMOTIONAL_CREDIT_APP }, computeFunding] as const,
  estimatedPayment: (creditApplicationId: string | null, state: string) =>
    [{ scope: ESTIMATED_PAYMENT }, creditApplicationId, state] as const,
}

const getPromotionalCreditAppPlan = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForPromotionalCreditApp']>>) => {
  const response = await apiClient.post<ComputeFundingResultDto>(
    `personal/CreditApplication/${encodeURIComponent(computeParams.creditApplicationId)}/computeFunding`,
    computeParams,
  )
  return response.data
}

export function useGetPromotionalCreditAppPlan(
  amountRequested: number,
  creditApplicationId: string,
  paymentPlanId: string | null,
): [ComputeFundingResultDto | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.amountForPromotionalCreditApp({
      amountRequested,
      creditApplicationId,
      paymentPlanId,
    } as ComputeFundingDto),
    queryFn: getPromotionalCreditAppPlan,
    enabled: !!creditApplicationId && Boolean(paymentPlanId),
  })

  return [data ?? null, isFetching]
}

const getEstimatedPayment = async ({
  queryKey: [_, creditApplicationId],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['estimatedPayment']>>) => {
  const response = await apiClient.get<ComputeEstimatedPaymentResultDto>(
    `personal/CreditApplication/${encodeURIComponent(creditApplicationId!)}/ComputeEstimatedPayment`,
  )
  return response.data
}

export function useComputeEstimatedPayment(
  creditApplicationId: string | null,
  state: string,
): [ComputeEstimatedPaymentResultDto | null, boolean, boolean] {
  const { data, isFetching, isError } = useQuery({
    queryKey: keysFactory.estimatedPayment(creditApplicationId, state),
    queryFn: getEstimatedPayment,
    enabled: creditApplicationId !== null,
  })

  return [data ?? null, isFetching, isError]
}
export type {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeFundingDto,
  ComputeFundingResultDto,
  MonthlyPayment,
  ComputeEstimatedPaymentResultDto
}
